import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Edit3,
  FileText,
  Plus,
  Search,
  Trash2,
  Upload,
  UserPlus,
  Users,
} from 'lucide-react';
import { Modal } from '../components/Modal';
import { ModuleHero } from '../components/ModuleHero';
import { ConfirmDialog, EventWorkspaceSection } from '../components/PageTemplates';
import { useStore } from '../hooks/useStore';
import type { Division, Profile, RosterEntry, Team } from '../models';
import { listProfiles } from '../services/directoryService';
import { getTournament, getTournamentBySlug } from '../services/tournamentService';
import { paths, tournamentSlug } from '../lib/paths';
import {
  addNewRosterPerson,
  addRosterProfile,
  createDivision,
  createTeam,
  deleteDivision,
  deleteTeam,
  getRosterEntryProfile,
  listDivisions,
  listTeams,
  removeRosterEntry,
  updateDivision,
  updateRosterEntryDetails,
  updateTeam,
} from '../services/rosterService';
import {
  buildRosterPreview,
  commitRosterImport,
  parseCsv,
  type RosterPreview,
} from '../services/rosterImport';

type RosterStatus = RosterEntry['status'];
type DivisionEditorState = { mode: 'create' } | { mode: 'edit'; division: Division };
type TeamEditorState = { mode: 'create'; divisionId?: string } | { mode: 'edit'; team: Team };

const STATUS_OPTIONS: Array<{ value: RosterStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'declined', label: 'Declined' },
  { value: 'no_show', label: 'No show' },
];

export function RosterManagerPage() {
  const { id = '' } = useParams();
  const t = useStore(() => getTournament(id) ?? getTournamentBySlug(id));
  useStore((d) => d);
  const [importOpen, setImportOpen] = useState(() => t?.settings.season?.rosterIntake === 'upload');
  const [preview, setPreview] = useState<RosterPreview | null>(null);
  const [importBanner, setImportBanner] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  if (!t) {
    return (
      <div className="empty">
        <div style={{ fontWeight: 600, color: 'var(--text)' }}>Competition not found</div>
        <Link to={paths.adminTournaments}>Back to events</Link>
      </div>
    );
  }

  const isSeason = t.format === 'season';

  const onFile = async (file: File) => {
    const text = await file.text();
    const parsed = parseCsv(text);
    setPreview(buildRosterPreview(parsed));
    setImportBanner(null);
  };

  const onCommit = () => {
    if (!preview) return;
    const result = commitRosterImport(t.id, preview);
    setImportBanner(
      `Imported ${result.playersAdded} player${result.playersAdded === 1 ? '' : 's'} into ${result.teamsCreated} new team${result.teamsCreated === 1 ? '' : 's'}. Skipped ${result.rowsSkipped} row${result.rowsSkipped === 1 ? '' : 's'} with errors or roster conflicts.`,
    );
    setPreview(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  return (
    <EventWorkspaceSection>
      <ModuleHero
        eyebrow={isSeason ? 'Season teams' : 'Tournament teams'}
        title="Teams"
        description={`Manage ${isSeason ? 'season' : 'tournament'} divisions, teams, players, roster intake status, and CSV imports from one event-specific workspace.`}
        icon={Users}
        accent="ice"
      >
        <Link to={paths.adminCompetition(t.format, tournamentSlug(t))} className="btn btn--ghost btn--sm">
          <ArrowLeft size={14} /> Back to workspace
        </Link>
      </ModuleHero>

      <div className="card roster-workspace">
        <div className="card__head">
          <div>
            <h3 className="card__title">Team management</h3>
            <p className="card__sub">Divisions, teams, player assignments, roster intake, and imports.</p>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={() => setImportOpen((open) => !open)}>
            <Upload size={14} /> {importOpen ? 'Hide CSV import' : 'CSV import'}
          </button>
        </div>

        {importOpen && (
          <UploadTab
            preview={preview}
            onFile={onFile}
            onCommit={onCommit}
            onClear={() => {
              setPreview(null);
              if (fileInput.current) fileInput.current.value = '';
            }}
            importBanner={importBanner}
            fileInputRef={fileInput}
          />
        )}
        <TeamManagementWorkbench
          tournamentId={t.id}
          noun={isSeason ? 'season' : 'tournament'}
          onOpenImport={() => setImportOpen(true)}
        />
      </div>
    </EventWorkspaceSection>
  );
}

function UploadTab({
  preview,
  onFile,
  onCommit,
  onClear,
  importBanner,
  fileInputRef,
}: {
  preview: RosterPreview | null;
  onFile: (f: File) => void;
  onCommit: () => void;
  onClear: () => void;
  importBanner: string | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
}) {
  return (
    <div className="stack">
      {importBanner && (
        <div className="note note--ok">
          <CheckCircle2 size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
          {importBanner}
        </div>
      )}

      <div className="note">
        <FileText size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
        Expected columns: <strong>Division, Team, Player, Email, Phone, Jersey, Position, Captain, Alternate</strong>. Manual and CSV roster entries both resolve to Directory people.
      </div>

      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
          className="input"
          style={{ maxWidth: 380 }}
        />
        {preview && (
          <button className="btn btn--ghost btn--sm" onClick={onClear}>
            Clear
          </button>
        )}
      </div>

      {preview && (
        <>
          <div className="grid grid--4">
            <StatTile label="Importable" value={preview.importableCount} tone="ok" />
            <StatTile label="With errors" value={preview.errorCount} tone="warn" />
            <StatTile label="Divisions" value={preview.divisions.length} />
            <StatTile label="Teams" value={Object.values(preview.teamsByDivision).reduce((n, l) => n + l.length, 0)} />
          </div>

          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Division</th>
                  <th>Team</th>
                  <th>Player</th>
                  <th>Jersey</th>
                  <th>Position</th>
                  <th>Captain</th>
                  <th>Issues</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 200).map((r) => (
                  <tr key={r.lineNumber} style={r.errors.length ? { background: 'rgba(255,85,119,0.06)' } : undefined}>
                    <td>{r.lineNumber}</td>
                    <td>{r.data.division ?? '-'}</td>
                    <td>{r.data.team ?? '-'}</td>
                    <td>{r.data.player_name ?? '-'}</td>
                    <td>{r.data.jersey_number ?? '-'}</td>
                    <td>{r.data.position ?? '-'}</td>
                    <td>{r.data.captain ? 'Yes' : ''}</td>
                    <td style={{ fontSize: 12 }}>
                      {r.errors.map((e) => (
                        <div key={e} style={{ color: 'var(--err)' }}>
                          <AlertTriangle size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                          {e}
                        </div>
                      ))}
                      {r.warnings.map((w) => (
                        <div key={w} className="muted">{w}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row" style={{ justifyContent: 'flex-end', gap: 8 }}>
            <button className="btn btn--primary" onClick={onCommit} disabled={preview.importableCount === 0}>
              Import {preview.importableCount} {preview.importableCount === 1 ? 'player' : 'players'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function TeamManagementWorkbench({
  tournamentId,
  noun,
  onOpenImport,
}: {
  tournamentId: string;
  noun: string;
  onOpenImport: () => void;
}) {
  const db = useStore((d) => d);
  const divisions = useMemo(() => listDivisions(tournamentId), [db.divisions, tournamentId]);
  const teams = useMemo(() => listTeams(tournamentId), [db.teams, tournamentId]);
  const rosterEntries = db.rosterEntries.filter((entry) => entry.tournamentId === tournamentId);
  const teamIds = new Set(teams.map((team) => team.id));
  const orphanedEntries = rosterEntries.filter((entry) => !teamIds.has(entry.teamId)).length;
  const emptyTeams = teams.filter((team) => !rosterEntries.some((entry) => entry.teamId === team.id)).length;
  const defaults = db.league.eventDefaults.divisionLabels;
  const [query, setQuery] = useState('');
  const [selectedDivisionId, setSelectedDivisionId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [divisionEditor, setDivisionEditor] = useState<DivisionEditorState | null>(null);
  const [teamEditor, setTeamEditor] = useState<TeamEditorState | null>(null);
  const [deleteDivisionTarget, setDeleteDivisionTarget] = useState<Division | null>(null);
  const [deleteTeamTarget, setDeleteTeamTarget] = useState<Team | null>(null);
  const existingNames = new Set(divisions.map((division) => division.name.trim().toLowerCase()));
  const selectedTeam = teams.find((team) => team.id === selectedTeamId);
  const selectedDivision = selectedTeam
    ? divisions.find((division) => division.id === selectedTeam.divisionId)
    : divisions.find((division) => division.id === selectedDivisionId);

  useEffect(() => {
    if (divisions.length === 0) {
      if (selectedDivisionId) setSelectedDivisionId('');
      return;
    }
    if (!selectedDivisionId || !divisions.some((division) => division.id === selectedDivisionId)) {
      setSelectedDivisionId(divisions[0].id);
    }
  }, [divisions, selectedDivisionId]);

  useEffect(() => {
    if (teams.length === 0) {
      if (selectedTeamId) setSelectedTeamId('');
      return;
    }
    if (!selectedTeamId || !teams.some((team) => team.id === selectedTeamId)) {
      const teamInDivision = teams.find((team) => team.divisionId === selectedDivisionId);
      setSelectedTeamId((teamInDivision ?? teams[0]).id);
    }
  }, [selectedDivisionId, selectedTeamId, teams]);

  const visibleDivisions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return divisions;
    return divisions.filter((division) => {
      const divisionMatches =
        division.name.toLowerCase().includes(needle) ||
        (division.bracketLabel ?? '').toLowerCase().includes(needle);
      return divisionMatches || teams.some((team) => {
        if (team.divisionId !== division.id) return false;
        return team.name.toLowerCase().includes(needle) || team.shortCode.toLowerCase().includes(needle);
      });
    });
  }, [divisions, query, teams]);

  const selectDivision = (division: Division) => {
    setSelectedDivisionId(division.id);
    setSelectedTeamId(teams.find((team) => team.divisionId === division.id)?.id ?? '');
  };

  return (
    <div className="stack roster-manager teams-workbench-wrap">
      <div className="grid grid--4">
        <StatTile label="Divisions" value={divisions.length} />
        <StatTile label="Teams" value={teams.length} />
        <StatTile label="Rostered players" value={rosterEntries.length} />
        <StatTile label={orphanedEntries ? 'Unassigned rows' : 'Empty teams'} value={orphanedEntries || emptyTeams} tone={orphanedEntries ? 'warn' : undefined} />
      </div>

      {divisions.length === 0 ? (
        <div className="empty">
          <div style={{ fontWeight: 600, color: 'var(--text)' }}>No divisions yet</div>
          <p>Add at least one division or import a CSV to start building this {noun} teams workspace.</p>
          <div className="row" style={{ gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn--primary btn--sm" onClick={() => setDivisionEditor({ mode: 'create' })}>
              <Plus size={14} /> Add division
            </button>
            <button className="btn btn--ghost btn--sm" onClick={onOpenImport}>
              <Upload size={14} /> Import CSV
            </button>
          </div>
        </div>
      ) : (
        <div className="teams-workbench">
          <aside className="teams-sidebar" aria-label="Division and team browser">
            <div className="teams-sidebar__head">
              <div>
                <h3 className="card__title">Divisions and teams</h3>
                <p className="card__sub">{teams.length} team{teams.length === 1 ? '' : 's'} across {divisions.length} division{divisions.length === 1 ? '' : 's'}.</p>
              </div>
              <button className="btn btn--primary btn--sm" onClick={() => setDivisionEditor({ mode: 'create' })}>
                <Plus size={14} /> Division
              </button>
            </div>

            <label className="field">
              <span className="field__label">Find division or team</span>
              <div className="roster-search">
                <Search size={16} />
                <input className="input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search teams, codes, divisions" />
              </div>
            </label>

            {defaults.length > 0 && (
              <div className="teams-defaults">
                <span className="muted" style={{ fontSize: 12 }}>Quick add:</span>
                {defaults.map((label) => (
                  <button
                    key={label}
                    type="button"
                    className="btn btn--ghost btn--sm"
                    disabled={existingNames.has(label.trim().toLowerCase())}
                    onClick={() => {
                      const division = createDivision({ tournamentId, name: label });
                      setSelectedDivisionId(division.id);
                      setSelectedTeamId('');
                    }}
                  >
                    <Plus size={12} /> {label}
                  </button>
                ))}
              </div>
            )}

            <div className="teams-browser-list touch-scroll">
              {visibleDivisions.length === 0 ? (
                <div className="empty roster-empty">No divisions or teams match that search.</div>
              ) : visibleDivisions.map((division) => {
                const divisionTeams = teams.filter((team) => team.divisionId === division.id);
                const needle = query.trim().toLowerCase();
                const filteredTeams = needle
                  ? divisionTeams.filter((team) => {
                    const divisionMatches =
                      division.name.toLowerCase().includes(needle) ||
                      (division.bracketLabel ?? '').toLowerCase().includes(needle);
                    return divisionMatches || team.name.toLowerCase().includes(needle) || team.shortCode.toLowerCase().includes(needle);
                  })
                  : divisionTeams;
                const playersInDivision = rosterEntries.filter((entry) => divisionTeams.some((team) => team.id === entry.teamId)).length;
                return (
                  <section key={division.id} className="teams-browser-division">
                    <button
                      type="button"
                      className={`teams-browser-division__button ${selectedDivision?.id === division.id && !selectedTeam ? 'is-active' : ''}`}
                      onClick={() => selectDivision(division)}
                    >
                      <span>
                        <strong>{division.name}</strong>
                        <small>{divisionTeams.length} team{divisionTeams.length === 1 ? '' : 's'} · {playersInDivision} player{playersInDivision === 1 ? '' : 's'}</small>
                      </span>
                    </button>
                    <div className="teams-browser-actions">
                      <button className="btn btn--ghost btn--sm" onClick={() => setTeamEditor({ mode: 'create', divisionId: division.id })}>
                        <Plus size={13} /> Team
                      </button>
                      <button className="btn btn--ghost btn--sm" onClick={() => setDivisionEditor({ mode: 'edit', division })}>
                        <Edit3 size={13} /> Edit
                      </button>
                    </div>
                    <div className="teams-browser-teams">
                      {filteredTeams.length === 0 ? (
                        <button type="button" className="roster-empty roster-empty--button" onClick={() => setTeamEditor({ mode: 'create', divisionId: division.id })}>
                          Add the first team.
                        </button>
                      ) : filteredTeams.map((team) => {
                        const teamRosterCount = rosterEntries.filter((entry) => entry.teamId === team.id).length;
                        return (
                          <button
                            key={team.id}
                            type="button"
                            className={`teams-browser-team ${team.id === selectedTeamId ? 'is-active' : ''}`}
                            onClick={() => {
                              setSelectedDivisionId(division.id);
                              setSelectedTeamId(team.id);
                            }}
                          >
                            <span className="team-color-dot" style={{ background: team.primaryColor }} />
                            <span>
                              <strong>{team.name}</strong>
                              <small>{team.shortCode} · {teamRosterCount} player{teamRosterCount === 1 ? '' : 's'}</small>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          </aside>

          <section className="teams-detail">
            {selectedDivision && (
              <DivisionDetailCard
                division={selectedDivision}
                teams={teams.filter((team) => team.divisionId === selectedDivision.id)}
                rosterEntries={rosterEntries}
                onAddTeam={() => setTeamEditor({ mode: 'create', divisionId: selectedDivision.id })}
                onEdit={() => setDivisionEditor({ mode: 'edit', division: selectedDivision })}
                onDelete={() => setDeleteDivisionTarget(selectedDivision)}
              />
            )}

            {selectedTeam && selectedDivision ? (
              <SelectedTeamPanel
                tournamentId={tournamentId}
                team={selectedTeam}
                division={selectedDivision}
                teams={teams}
                rosterEntries={rosterEntries}
                onEditTeam={() => setTeamEditor({ mode: 'edit', team: selectedTeam })}
                onDeleteTeam={() => setDeleteTeamTarget(selectedTeam)}
              />
            ) : (
              <div className="empty teams-empty">
                <div style={{ fontWeight: 600, color: 'var(--text)' }}>Select a team</div>
                <p>Choose a team from the browser, or add one to the selected division.</p>
              </div>
            )}
          </section>
        </div>
      )}

      <DivisionEditorModal
        state={divisionEditor}
        tournamentId={tournamentId}
        onClose={() => setDivisionEditor(null)}
        onSaved={(division) => {
          setSelectedDivisionId(division.id);
          setDivisionEditor(null);
        }}
      />
      <TeamEditorModal
        state={teamEditor}
        tournamentId={tournamentId}
        divisions={divisions}
        onClose={() => setTeamEditor(null)}
        onSaved={(team) => {
          setSelectedDivisionId(team.divisionId);
          setSelectedTeamId(team.id);
          setTeamEditor(null);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleteDivisionTarget)}
        title={`Delete ${deleteDivisionTarget?.name ?? 'this division'}?`}
        body="This removes the division, its teams, and roster entries. Scored game events will not be deleted."
        confirmLabel="Delete division"
        tone="danger"
        onCancel={() => setDeleteDivisionTarget(null)}
        onConfirm={() => {
          if (!deleteDivisionTarget) return;
          deleteDivision(deleteDivisionTarget.id);
          setDeleteDivisionTarget(null);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleteTeamTarget)}
        title={`Delete ${deleteTeamTarget?.name ?? 'this team'}?`}
        body="This removes the team and its roster entries. Scored game events will not be deleted."
        confirmLabel="Delete team"
        tone="danger"
        onCancel={() => setDeleteTeamTarget(null)}
        onConfirm={() => {
          if (!deleteTeamTarget) return;
          deleteTeam(deleteTeamTarget.id);
          setDeleteTeamTarget(null);
        }}
      />
    </div>
  );
}

function DivisionDetailCard({
  division,
  teams,
  rosterEntries,
  onAddTeam,
  onEdit,
  onDelete,
}: {
  division: Division;
  teams: Team[];
  rosterEntries: RosterEntry[];
  onAddTeam: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const teamIds = new Set(teams.map((team) => team.id));
  const playerCount = rosterEntries.filter((entry) => teamIds.has(entry.teamId)).length;

  return (
    <article className="team-summary-card">
      <div className="card__head">
        <div>
          <div className="eyebrow">Selected division</div>
          <h3 className="card__title">{division.name}</h3>
          <p className="card__sub">
            {division.bracketLabel || 'No bracket label'} · {teams.length} team{teams.length === 1 ? '' : 's'} · {playerCount} player{playerCount === 1 ? '' : 's'}
          </p>
        </div>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn--primary btn--sm" onClick={onAddTeam}>
            <Plus size={13} /> Add team
          </button>
          <button className="btn btn--ghost btn--sm" onClick={onEdit}>
            <Edit3 size={13} /> Edit division
          </button>
          <button className="btn btn--danger btn--sm" onClick={onDelete}>
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>
    </article>
  );
}

function SelectedTeamPanel({
  tournamentId,
  team,
  division,
  teams,
  rosterEntries,
  onEditTeam,
  onDeleteTeam,
}: {
  tournamentId: string;
  team: Team;
  division: Division;
  teams: Team[];
  rosterEntries: RosterEntry[];
  onEditTeam: () => void;
  onDeleteTeam: () => void;
}) {
  const db = useStore((d) => d);
  const roster = rosterEntries
    .filter((entry) => entry.teamId === team.id)
    .sort((a, b) => a.jerseyNumber - b.jerseyNumber);
  const captainProfile = team.captainProfileId ? db.profiles.find((profile) => profile.id === team.captainProfileId) : undefined;
  const [addOpen, setAddOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<RosterEntry | null>(null);

  return (
    <article className="roster-team teams-selected-team">
      <div className="roster-team__head">
        <div className="teams-selected-team__identity">
          {team.logoUrl ? (
            <img src={team.logoUrl} alt="" className="team-logo-preview" />
          ) : (
            <span className="team-color-swatch" style={{ background: team.primaryColor }} />
          )}
          <div style={{ minWidth: 0 }}>
            <h3>{team.name}</h3>
            <p>
              {division.name} · {team.shortCode} · {roster.length} player{roster.length === 1 ? '' : 's'}
            </p>
            <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              <span className="chip">Primary {team.primaryColor}</span>
              {team.secondaryColor && <span className="chip">Secondary {team.secondaryColor}</span>}
              {captainProfile && <span className="chip chip--info">Captain: {captainProfile.displayName}</span>}
            </div>
          </div>
        </div>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <button className="btn btn--primary btn--sm" onClick={() => setAddOpen(true)}>
            <UserPlus size={14} /> Add player
          </button>
          <button className="btn btn--ghost btn--sm" onClick={onEditTeam}>
            <Edit3 size={13} /> Edit team
          </button>
          <button className="btn btn--danger btn--sm" onClick={onDeleteTeam}>
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>

      {roster.length === 0 ? (
        <button type="button" className="roster-empty roster-empty--button" onClick={() => setAddOpen(true)}>
          Add the first player from Directory.
        </button>
      ) : (
        <div className="table-scroll">
          <table className="table table--clickable roster-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Position</th>
                <th>Status</th>
                <th>Intake</th>
                <th>Flags</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {roster.map((entry) => {
                const profile = getRosterEntryProfile(entry);
                const isCaptain = Boolean(profile && team.captainProfileId === profile.id);
                return (
                  <tr
                    key={entry.id}
                    tabIndex={0}
                    onClick={() => setSelectedEntry(entry)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') setSelectedEntry(entry);
                    }}
                  >
                    <td>{entry.jerseyNumber}</td>
                    <td>
                      <strong>{profile?.displayName ?? 'Unknown player'}</strong>
                      {profile?.email && <div className="muted" style={{ fontSize: 12 }}>{profile.email}</div>}
                    </td>
                    <td>{entry.position || '-'}</td>
                    <td><span className={`chip ${entry.status === 'confirmed' ? 'chip--ok' : entry.status === 'declined' || entry.status === 'no_show' ? 'chip--warn' : ''}`}>{statusLabel(entry.status)}</span></td>
                    <td>
                      <PlayerIntakeBadges tournamentId={tournamentId} profileId={profile?.id} />
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {isCaptain && <span className="chip chip--info">Captain</span>}
                        {entry.isAlternate && <span className="chip">Alternate</span>}
                      </div>
                    </td>
                    <td>
                      <button
                        className="btn btn--ghost btn--sm"
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedEntry(entry);
                        }}
                      >
                        <Edit3 size={13} /> Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AddRosterPersonModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        tournamentId={tournamentId}
        team={team}
      />
      <EditRosterEntryModal
        entry={selectedEntry}
        teams={teams}
        onClose={() => setSelectedEntry(null)}
      />
    </article>
  );
}

function DivisionEditorModal({
  state,
  tournamentId,
  onClose,
  onSaved,
}: {
  state: DivisionEditorState | null;
  tournamentId: string;
  onClose: () => void;
  onSaved: (division: Division) => void;
}) {
  const db = useStore((d) => d);
  const current = state?.mode === 'edit' ? db.divisions.find((division) => division.id === state.division.id) ?? state.division : null;
  const [name, setName] = useState('');
  const [bracketLabel, setBracketLabel] = useState('');

  useEffect(() => {
    if (!state) return;
    setName(current?.name ?? '');
    setBracketLabel(current?.bracketLabel ?? '');
  }, [current?.id, current?.name, current?.bracketLabel, state?.mode]);

  const canSubmit = Boolean(name.trim());
  const submit = () => {
    if (!state || !canSubmit) return;
    const patch = {
      name: name.trim(),
      bracketLabel: bracketLabel.trim() || undefined,
    };
    const saved = state.mode === 'edit'
      ? updateDivision(state.division.id, patch)
      : createDivision({ tournamentId, ...patch });
    if (saved) onSaved(saved);
  };

  return (
    <Modal
      open={Boolean(state)}
      onClose={onClose}
      title={state?.mode === 'edit' ? 'Edit division' : 'Add division'}
      subtitle="Divisions group teams for scheduling, standings, and bracket labels."
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={submit} disabled={!canSubmit}>
            {state?.mode === 'edit' ? 'Save division' : 'Create division'}
          </button>
        </>
      }
    >
      <div className="stack">
        <label className="field">
          <span className="field__label">Division name</span>
          <input className="input" value={name} onChange={(event) => setName(event.target.value)} placeholder="Bronze, Gold, Open A" />
        </label>
        <label className="field">
          <span className="field__label">Bracket label optional</span>
          <input className="input" value={bracketLabel} onChange={(event) => setBracketLabel(event.target.value)} placeholder="Pool A, Varsity, 18U" />
        </label>
      </div>
    </Modal>
  );
}

function TeamEditorModal({
  state,
  tournamentId,
  divisions,
  onClose,
  onSaved,
}: {
  state: TeamEditorState | null;
  tournamentId: string;
  divisions: Division[];
  onClose: () => void;
  onSaved: (team: Team) => void;
}) {
  const db = useStore((d) => d);
  const current = state?.mode === 'edit' ? db.teams.find((team) => team.id === state.team.id) ?? state.team : null;
  const [divisionId, setDivisionId] = useState('');
  const [name, setName] = useState('');
  const [shortCode, setShortCode] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#7c5cff');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [captainProfileId, setCaptainProfileId] = useState('');

  useEffect(() => {
    if (!state) return;
    const defaultDivisionId = state.mode === 'create' ? state.divisionId ?? divisions[0]?.id ?? '' : current?.divisionId ?? '';
    setDivisionId(defaultDivisionId);
    setName(current?.name ?? '');
    setShortCode(current?.shortCode ?? '');
    setPrimaryColor(current?.primaryColor ?? '#7c5cff');
    setSecondaryColor(current?.secondaryColor ?? '');
    setLogoUrl(current?.logoUrl ?? '');
    setCaptainProfileId(current?.captainProfileId ?? '');
  }, [
    current?.id,
    current?.divisionId,
    current?.name,
    current?.shortCode,
    current?.primaryColor,
    current?.secondaryColor,
    current?.logoUrl,
    current?.captainProfileId,
    divisions,
    state,
  ]);

  const rosterCaptainOptions = current
    ? db.rosterEntries
      .filter((entry) => entry.teamId === current.id)
      .map((entry) => getRosterEntryProfile(entry))
      .filter((profile): profile is Profile => Boolean(profile))
      .sort((a, b) => a.displayName.localeCompare(b.displayName))
    : [];
  const canSubmit = Boolean(name.trim() && divisionId);

  const submit = () => {
    if (!state || !canSubmit) return;
    const patch = {
      tournamentId,
      divisionId,
      name: name.trim(),
      shortCode: (shortCode.trim() || name.replace(/[^a-zA-Z]/g, '').slice(0, 3)).toUpperCase(),
      primaryColor,
      secondaryColor: secondaryColor || undefined,
      logoUrl: logoUrl.trim() || undefined,
      captainProfileId: captainProfileId || undefined,
    };
    const saved = state.mode === 'edit'
      ? updateTeam(state.team.id, patch)
      : createTeam(patch);
    if (saved) onSaved(saved);
  };

  return (
    <Modal
      open={Boolean(state)}
      onClose={onClose}
      title={state?.mode === 'edit' ? 'Edit team' : 'Add team'}
      subtitle="Team settings are scoped to this event workspace."
      size="lg"
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={submit} disabled={!canSubmit}>
            {state?.mode === 'edit' ? 'Save team' : 'Create team'}
          </button>
        </>
      }
    >
      <div className="grid grid--2">
        <label className="field">
          <span className="field__label">Team name</span>
          <input className="input" value={name} onChange={(event) => setName(event.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Short code</span>
          <input className="input" value={shortCode} onChange={(event) => setShortCode(event.target.value.toUpperCase())} placeholder="TE" />
        </label>
        <label className="field">
          <span className="field__label">Division</span>
          <select className="select" value={divisionId} onChange={(event) => setDivisionId(event.target.value)}>
            {divisions.map((division) => <option key={division.id} value={division.id}>{division.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Logo URL optional</span>
          <input className="input" value={logoUrl} onChange={(event) => setLogoUrl(event.target.value)} placeholder="https://..." />
        </label>
        <label className="field">
          <span className="field__label">Primary color</span>
          <input className="input" type="color" value={primaryColor} onChange={(event) => setPrimaryColor(event.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Secondary color optional</span>
          <input className="input" type="color" value={secondaryColor || '#ffffff'} onChange={(event) => setSecondaryColor(event.target.value)} />
        </label>
        {state?.mode === 'edit' && (
          <label className="field">
            <span className="field__label">Captain</span>
            <select className="select" value={captainProfileId} onChange={(event) => setCaptainProfileId(event.target.value)}>
              <option value="">No captain selected</option>
              {rosterCaptainOptions.map((profile) => (
                <option key={profile.id} value={profile.id}>{profile.displayName}</option>
              ))}
            </select>
          </label>
        )}
      </div>
    </Modal>
  );
}

function PlayerIntakeBadges({ tournamentId, profileId }: { tournamentId: string; profileId?: string }) {
  const db = useStore((d) => d);
  if (!profileId) return <span className="chip">No Directory profile</span>;
  const registration = db.registrations.find((row) => row.tournamentId === tournamentId && row.profileId === profileId);
  const payment = registration
    ? db.paymentIntents.find((row) => row.id === registration.paymentIntentId || row.registrationId === registration.id)
    : db.paymentIntents.find((row) => row.tournamentId === tournamentId && row.metadata?.profileId === profileId);

  if (!registration && !payment) return <span className="chip">No intake record</span>;

  return (
    <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
      {registration && (
        <span className={`chip ${registration.status === 'approved' ? 'chip--ok' : registration.status === 'declined' || registration.status === 'withdrawn' ? 'chip--warn' : ''}`}>
          {registration.status.replace('_', ' ')}
        </span>
      )}
      {payment && (
        <span className={`chip ${payment.status === 'succeeded' ? 'chip--ok' : payment.status === 'failed' || payment.status === 'refunded' ? 'chip--warn' : ''}`}>
          Payment {payment.status.replace('_', ' ')}
        </span>
      )}
    </div>
  );
}

function AddRosterPersonModal({
  open,
  onClose,
  tournamentId,
  team,
}: {
  open: boolean;
  onClose: () => void;
  tournamentId: string;
  team: Team;
}) {
  const db = useStore((d) => d);
  const [mode, setMode] = useState<'directory' | 'new'>('directory');
  const [query, setQuery] = useState('');
  const [profileId, setProfileId] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jersey, setJersey] = useState('');
  const [position, setPosition] = useState('');
  const [captain, setCaptain] = useState(false);
  const [alternate, setAlternate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const profiles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return listProfiles()
      .filter((profile) => {
        if (!needle) return true;
        return profile.displayName.toLowerCase().includes(needle) || (profile.email ?? '').toLowerCase().includes(needle);
      })
      .slice(0, 20);
  }, [db.profiles, query]);

  const reset = () => {
    setMode('directory');
    setQuery('');
    setProfileId('');
    setDisplayName('');
    setEmail('');
    setPhone('');
    setJersey('');
    setPosition('');
    setCaptain(false);
    setAlternate(false);
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const jerseyNumber = Number(jersey);
  const validJersey = jersey.trim() !== '' && Number.isFinite(jerseyNumber) && jerseyNumber >= 0;
  const canSubmit = validJersey && (mode === 'directory' ? Boolean(profileId) : Boolean(displayName.trim()));

  const submit = () => {
    if (!canSubmit) return;
    const result = mode === 'directory'
      ? addRosterProfile({
        tournamentId,
        teamId: team.id,
        profileId,
        jerseyNumber,
        position,
        isCaptain: captain,
        isAlternate: alternate,
        status: 'pending',
      })
      : addNewRosterPerson({
        tournamentId,
        teamId: team.id,
        displayName: displayName.trim(),
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        jerseyNumber,
        position,
        isCaptain: captain,
        isAlternate: alternate,
      });

    if (result.error) {
      setError(result.error);
      return;
    }
    close();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Add player to ${team.name}`}
      subtitle="Roster people from Directory first, or explicitly add a new Directory person."
      size="lg"
      footer={
        <>
          <button className="btn btn--ghost" onClick={close}>Cancel</button>
          <button className="btn btn--primary" onClick={submit} disabled={!canSubmit}>Add to team</button>
        </>
      }
    >
      <div className="stack">
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button className={`btn ${mode === 'directory' ? 'btn--primary' : 'btn--ghost'} btn--sm`} onClick={() => { setMode('directory'); setError(null); }}>
            Directory person
          </button>
          <button className={`btn ${mode === 'new' ? 'btn--primary' : 'btn--ghost'} btn--sm`} onClick={() => { setMode('new'); setProfileId(''); setError(null); }}>
            Add new person
          </button>
        </div>

        {error && <div className="note note--warn">{error}</div>}

        {mode === 'directory' ? (
          <div className="stack">
            <label className="field">
              <span className="field__label">Search Directory</span>
              <div className="roster-search">
                <Search size={16} />
                <input className="input" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name or email" />
              </div>
            </label>
            <div className="roster-directory-list touch-scroll">
              {profiles.length === 0 ? (
                <div className="empty roster-empty">No active Directory people match that search.</div>
              ) : profiles.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  className={`roster-directory-row ${profileId === profile.id ? 'is-selected' : ''}`}
                  onClick={() => { setProfileId(profile.id); setError(null); }}
                >
                  <span>
                    <strong>{profile.displayName}</strong>
                    <span>{profile.email || 'No email on file'}</span>
                  </span>
                  {profileId === profile.id && <CheckCircle2 size={16} />}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid--2">
            <label className="field">
              <span className="field__label">Display name</span>
              <input className="input" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Email optional</span>
              <input className="input" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
            </label>
            <label className="field">
              <span className="field__label">Phone optional</span>
              <input className="input" type="tel" placeholder="(555) 123-4567" value={phone} onChange={(event) => setPhone(event.target.value)} />
            </label>
          </div>
        )}

        <RosterFields
          jersey={jersey}
          onJersey={setJersey}
          position={position}
          onPosition={setPosition}
          captain={captain}
          onCaptain={setCaptain}
          alternate={alternate}
          onAlternate={setAlternate}
        />
        {!validJersey && jersey.trim() !== '' && <span className="field__error">Enter a valid jersey number.</span>}
      </div>
    </Modal>
  );
}

function EditRosterEntryModal({
  entry,
  teams,
  onClose,
}: {
  entry: RosterEntry | null;
  teams: Team[];
  onClose: () => void;
}) {
  const db = useStore((d) => d);
  const current = entry ? db.rosterEntries.find((row) => row.id === entry.id) ?? entry : null;
  const profile = current ? getRosterEntryProfile(current) : undefined;
  const currentTeam = current ? db.teams.find((team) => team.id === current.teamId) : undefined;
  const [teamId, setTeamId] = useState('');
  const [jersey, setJersey] = useState('');
  const [position, setPosition] = useState('');
  const [captain, setCaptain] = useState(false);
  const [alternate, setAlternate] = useState(false);
  const [status, setStatus] = useState<RosterStatus>('pending');
  const [error, setError] = useState<string | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);

  useEffect(() => {
    if (!current) return;
    setTeamId(current.teamId);
    setJersey(String(current.jerseyNumber));
    setPosition(current.position ?? '');
    setAlternate(current.isAlternate);
    setStatus(current.status);
    setCaptain(Boolean(profile && currentTeam?.captainProfileId === profile.id));
    setError(null);
  }, [current?.id, profile?.id, currentTeam?.captainProfileId]);

  const jerseyNumber = Number(jersey);
  const canSubmit = Boolean(current && teamId && jersey.trim() !== '' && Number.isFinite(jerseyNumber) && jerseyNumber >= 0);

  const submit = () => {
    if (!current || !canSubmit) return;
    const result = updateRosterEntryDetails(current.id, {
      teamId,
      jerseyNumber,
      position,
      isAlternate: alternate,
      isCaptain: captain,
      status,
    });
    if (result.error) {
      setError(result.error);
      return;
    }
    onClose();
  };

  const remove = () => {
    if (!current) return;
    removeRosterEntry(current.id);
    setRemoveOpen(false);
    onClose();
  };

  return (
    <Modal
      open={Boolean(current)}
      onClose={onClose}
      title={profile?.displayName ?? 'Edit roster entry'}
      subtitle="Roster edits affect the event roster going forward. Scored game history stays intact."
      size="lg"
      footer={
        <>
          <button className="btn btn--danger" onClick={() => setRemoveOpen(true)}>Remove from roster</button>
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={submit} disabled={!canSubmit}>Save changes</button>
        </>
      }
    >
      {current && (
        <div className="stack">
          {error && <div className="note note--warn">{error}</div>}
          {profile && (
            <div className="note">
              Contact and full profile edits stay in Directory.{' '}
              <Link to={paths.adminDirectoryProfile(profile.id)}>Open Directory profile</Link>
            </div>
          )}
          <label className="field">
            <span className="field__label">Team</span>
            <select className="select" value={teamId} onChange={(event) => setTeamId(event.target.value)}>
              {teams.map((team) => {
                const division = db.divisions.find((row) => row.id === team.divisionId);
                return <option key={team.id} value={team.id}>{division?.name ?? 'Division'} - {team.name}</option>;
              })}
            </select>
          </label>
          <RosterFields
            jersey={jersey}
            onJersey={setJersey}
            position={position}
            onPosition={setPosition}
            captain={captain}
            onCaptain={setCaptain}
            alternate={alternate}
            onAlternate={setAlternate}
          />
          <label className="field">
            <span className="field__label">Roster status</span>
            <select className="select" value={status} onChange={(event) => setStatus(event.target.value as RosterStatus)}>
              {STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
        </div>
      )}
      <ConfirmDialog
        open={removeOpen}
        title={`Remove ${profile?.displayName ?? 'this player'} from the roster?`}
        body="This removes the event roster entry only. It will not delete the Directory profile or scored game history."
        confirmLabel="Remove player"
        tone="danger"
        onCancel={() => setRemoveOpen(false)}
        onConfirm={remove}
      />
    </Modal>
  );
}

function RosterFields({
  jersey,
  onJersey,
  position,
  onPosition,
  captain,
  onCaptain,
  alternate,
  onAlternate,
}: {
  jersey: string;
  onJersey: (value: string) => void;
  position: string;
  onPosition: (value: string) => void;
  captain: boolean;
  onCaptain: (value: boolean) => void;
  alternate: boolean;
  onAlternate: (value: boolean) => void;
}) {
  return (
    <div className="grid grid--2">
      <label className="field">
        <span className="field__label">Jersey number</span>
        <input className="input" type="number" min={0} value={jersey} onChange={(event) => onJersey(event.target.value)} />
      </label>
      <label className="field">
        <span className="field__label">Position</span>
        <input className="input" value={position} onChange={(event) => onPosition(event.target.value)} placeholder="F, D, G, or custom" />
      </label>
      <label className="roster-toggle">
        <input type="checkbox" checked={captain} onChange={(event) => onCaptain(event.target.checked)} />
        <span>
          <strong>Captain</strong>
          <small>Sets team captain and event captain role.</small>
        </span>
      </label>
      <label className="roster-toggle">
        <input type="checkbox" checked={alternate} onChange={(event) => onAlternate(event.target.checked)} />
        <span>
          <strong>Alternate</strong>
          <small>Marks this player as an alternate roster entry.</small>
        </span>
      </label>
    </div>
  );
}

function StatTile({ label, value, tone }: { label: string; value: number; tone?: 'ok' | 'warn' }) {
  return (
    <div className="stat">
      <div className="stat__label">{label}</div>
      <div className="stat__value" style={tone === 'ok' ? { color: 'var(--ok)' } : tone === 'warn' ? { color: 'var(--warn)' } : undefined}>{value}</div>
    </div>
  );
}

function statusLabel(status: RosterStatus): string {
  return STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status;
}
